import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { issuesApi } from '../api/issues';
import { getInstancesByIssue, updateItemInstance, reorderInstances } from '../api/items';
import { useSortableArticles, SortableItem, SortableCategory } from '../hooks/useSortableArticles.jsx';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { useAuthStore } from '../store/authStore';
import { useSlideOverStore } from '../store/slideOverStore';
import SlideOverPanel from '../components/SlideOverPanel';
import ItemDetailPage from './ItemDetailPage';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  DocumentTextIcon,
  VideoCameraIcon,
  SpeakerWaveIcon,
  PhotoIcon,
  QuestionMarkCircleIcon,
  NewspaperIcon,
  XCircleIcon,
  ChartBarIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardIcon,
  CheckIcon,
  SparklesIcon,
  CheckBadgeIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  FlagIcon,
  ArrowPathIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { FlagIcon as FlagIconSolid } from '@heroicons/react/24/solid';
import { ITEM_CATEGORIES, getCategoryColor, getCategoryLabel, INSTANCE_FLAGS, getFlagOption } from '../constants/items';

// Keyboard shortcuts for flag filter (mirrors keydown handler in IssuesPage)
const FLAG_FILTER_SHORTCUTS = { new: 'N', review: 'R', followup: 'F' };

// Custom bullseye icon for relevance score
const BullseyeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
  </svg>
);

// Score color helper - matches ItemDetailPage
const getScoreColor = (scoreValue) => {
  if (scoreValue < 2.5) return 'bg-red-500';
  if (scoreValue < 3.5) return 'bg-yellow-500';
  return 'bg-green-500';
};

// Score bar component matching ItemDetailPage
const ScoreBar = ({ barIndex, score }) => {
  const fillPercent = Math.max(0, Math.min(1, score - (barIndex - 1))) * 100;
  const fillColor = getScoreColor(score);
  return (
    <div className="w-3.5 h-2.5 rounded-sm bg-gray-200 dark:bg-gray-700 overflow-hidden">
      {fillPercent > 0 && (
        <div className={`h-full ${fillColor}`} style={{ width: `${fillPercent}%` }} />
      )}
    </div>
  );
};

// Score display with 5 bars - matches ItemDetailPage
const ScoreDisplay = ({ score }) => {
  if (score == null) return null;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((bar) => (
        <ScoreBar key={bar} barIndex={bar} score={score} />
      ))}
    </div>
  );
};
import NewsSectionGeneratorModal from '../components/NewsSectionGeneratorModal';
import IssueIntroGenerator from '../components/IssueIntroGenerator';
import DeepDiveSection from '../components/DeepDiveSection';
import { generateNewsSection, generateIssueIntro, generateDeepDive } from '../api/blurb';

// Mirrors backend `resolveEmailSubject` so the preview shows what SendGrid will receive.
function resolveEmailSubject(issue) {
  if (!issue?.subject) return '';
  return issue.subject
    .replaceAll('{issueNumber}', String(issue.issueNumber ?? ''))
    .replaceAll('{issueTitle}', issue.title ?? '');
}

function EmailPreviewIframe({ issue }) {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const issueNumber = issue?.issueNumber;
  const resolvedSubject = resolveEmailSubject(issue);
  const previewText = issue?.previewText;

  useEffect(() => {
    let revoked = false;
    let blobUrl = null;

    issuesApi.previewSingleSend(issueNumber)
      .then((html) => {
        if (revoked) return;
        const blob = new Blob([html], { type: 'text/html' });
        blobUrl = URL.createObjectURL(blob);
        if (iframeRef.current) {
          iframeRef.current.src = blobUrl;
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!revoked) {
          setError(err.response?.data?.error || err.message || 'Failed to load preview');
          setLoading(false);
        }
      });

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [issueNumber]);

  if (error) {
    return <div className="p-4 text-sm text-red-600 dark:text-red-400">Preview error: {error}</div>;
  }

  return (
    <>
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 shrink-0">Subject</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">
            {resolvedSubject || <span className="italic font-normal text-gray-400 dark:text-gray-500">No subject set</span>}
          </span>
        </div>
        {previewText && (
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 shrink-0">Preview</span>
            <span className="text-xs text-gray-600 dark:text-gray-300 break-words">{previewText}</span>
          </div>
        )}
      </div>
      {loading && <div className="p-4 text-sm text-gray-400 dark:text-gray-500">Loading preview...</div>}
      <iframe
        ref={iframeRef}
        title={`Email Preview: Issue #${issueNumber}`}
        className={`w-full h-[600px] border-0 ${loading ? 'hidden' : ''}`}
        sandbox="allow-same-origin"
      />
    </>
  );
}

function IssuesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { issueNumber } = useParams();
  const { user } = useAuthStore();

  // Slide-over panel state for item details
  const {
    isOpen: isSlideOverOpen,
    itemHash: slideOverItemHash,
    openSlideOver,
    closeSlideOver,
    navigatePrevious,
    navigateNext,
    getPosition,
  } = useSlideOverStore();

  const [isCreating, setIsCreating] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [editingIssueField, setEditingIssueField] = useState(null); // tracks which issue field is being edited
  const [assignedItemsTab, setAssignedItemsTab] = useState('edit');
  const [copySuccess, setCopySuccess] = useState(false);
  const [editingField, setEditingField] = useState(null); // tracks which field is being edited: format "itemId-fieldName"
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(null); // tracks which item's category dropdown is open
  const [expandedCategorySelector, setExpandedCategorySelector] = useState(null); // tracks which item's expandable category selector is open
  const [hoveredFlagItem, setHoveredFlagItem] = useState(null); // tracks which item's flag selector is being hovered
  const [showStatusDropdown, setShowStatusDropdown] = useState(false); // tracks if issue status dropdown is open
  const [collapsedCategories, setCollapsedCategories] = useState({
    '': false, // No Category (unassigned) - start expanded
    'news': true,
    'tutorial': true,
    'reads': true,
    'multimedia': true,
    'aws': true,
    'social': true,
    'tools': true,
    'security': true,
  }); // tracks which category sections are collapsed
  const [newIssueNumber, setNewIssueNumber] = useState(''); // tracks issue number input for new issue
  const [newIssueDate, setNewIssueDate] = useState(''); // tracks issue date input for new issue
  const [flagFilter, setFlagFilter] = useState(null); // null = all, or specific flag value to filter by
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [contentExpanded, setContentExpanded] = useState(false);
  const [contentOverflows, setContentOverflows] = useState(false);
  const contentPreviewRef = useRef(null);

  // AI Generation state
  const [showNewsSectionGenerator, setShowNewsSectionGenerator] = useState(false);
  const [isGeneratingNewsSection, setIsGeneratingNewsSection] = useState(false);
  const [generatedNewsBlurbs, setGeneratedNewsBlurbs] = useState([]);
  const [newsSectionError, setNewsSectionError] = useState(null);

  const [isGeneratingIntro, setIsGeneratingIntro] = useState(false);
  const [generatedIntro, setGeneratedIntro] = useState(null);
  const [generatedTitles, setGeneratedTitles] = useState([]);
  const [introError, setIntroError] = useState(null);

  const [isGeneratingDeepDive, setIsGeneratingDeepDive] = useState(false);
  const [generatedDeepDive, setGeneratedDeepDive] = useState(null);
  const [deepDiveError, setDeepDiveError] = useState(null);

  const { data: issuesData, isLoading } = useQuery({
    queryKey: ['issues'],
    queryFn: () => issuesApi.list(),
  });

  // Query for deep-linked issue - always fetch fresh data when navigating to an issue
  const { data: deepLinkedIssue, isLoading: isLoadingDeepLinkedIssue } = useQuery({
    queryKey: ['issue', 'number', issueNumber],
    queryFn: () => issuesApi.getByIssueNumber(parseInt(issueNumber)),
    enabled: !!issueNumber,
    staleTime: 0, // Always consider data stale to ensure fresh fetch
  });

  // Query for ItemInstances assigned to the current issue - always fetch fresh data
  const { data: assignedItems, isLoading: isLoadingItems } = useQuery({
    queryKey: ['instances', 'issue', editingIssue?.issueNumber],
    queryFn: async () => {
      return await getInstancesByIssue(editingIssue.issueNumber, {
        limit: 100 // Get all instances for this issue
      });
    },
    enabled: !!editingIssue?.issueNumber,
    staleTime: 0, // Always consider data stale to ensure fresh fetch
  });

  // Handle deep link: when issue is loaded from URL, set it as viewing (not editing)
  // Also clear editingIssue when navigating back to main issues list
  useEffect(() => {
    if (issueNumber && deepLinkedIssue) {
      // Always update editingIssue when deepLinkedIssue changes to keep it in sync
      setEditingIssue(deepLinkedIssue);
    } else if (!issueNumber) {
      // Clear editingIssue when navigating back to main issues list
      setEditingIssue(null);
    }
  }, [deepLinkedIssue, issueNumber]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCategoryDropdown && !event.target.closest('.relative')) {
        setShowCategoryDropdown(null);
      }
      if (expandedCategorySelector && !event.target.closest('.category-selector')) {
        setExpandedCategorySelector(null);
      }
      if (hoveredFlagItem && !event.target.closest('.flag-selector')) {
        setHoveredFlagItem(null);
      }
      if (showStatusDropdown && !event.target.closest('.status-dropdown')) {
        setShowStatusDropdown(false);
      }
    };

    if (showCategoryDropdown || expandedCategorySelector || showStatusDropdown || hoveredFlagItem) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showCategoryDropdown, expandedCategorySelector, showStatusDropdown, hoveredFlagItem]);

  // Close expandable category selector on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && expandedCategorySelector) {
        setExpandedCategorySelector(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expandedCategorySelector]);

  // Issue editor keyboard shortcuts: expand/collapse all + flag filter
  useEffect(() => {
    if (!editingIssue || assignedItemsTab !== 'edit') return;

    const handleKeyDown = (e) => {
      const target = e.target;
      const isFormElement = target.tagName === 'INPUT' ||
                            target.tagName === 'TEXTAREA' ||
                            target.tagName === 'SELECT' ||
                            target.isContentEditable;
      if (isFormElement) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const key = e.key.toUpperCase();
      if (key === 'E') {
        e.preventDefault();
        expandAll();
      } else if (key === 'C') {
        e.preventDefault();
        collapseAll();
      } else if (key === 'A') {
        e.preventDefault();
        setFlagFilter(null);
      } else if (key === 'N') {
        e.preventDefault();
        setFlagFilter((prev) => (prev === 'new' ? null : 'new'));
        expandAll();
      } else if (key === 'R') {
        e.preventDefault();
        setFlagFilter((prev) => (prev === 'review' ? null : 'review'));
        expandAll();
      } else if (key === 'F') {
        e.preventDefault();
        setFlagFilter((prev) => (prev === 'followup' ? null : 'followup'));
        expandAll();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingIssue, assignedItemsTab]);
  
  // Reset content expansion + measure overflow when content or issue changes
  useEffect(() => {
    setContentExpanded(false);
  }, [editingIssue?.id]);

  useEffect(() => {
    if (!contentPreviewRef.current) {
      setContentOverflows(false);
      return;
    }
    const CONTENT_TRUNCATE_PX = 400;
    setContentOverflows(contentPreviewRef.current.scrollHeight > CONTENT_TRUNCATE_PX);
  }, [editingIssue?.content, editingIssueField]);

  const createMutation = useMutation({
    mutationFn: (data) => issuesApi.create(data),
    onSuccess: () => {
      // Invalidate all issues-related queries to ensure cache consistency
      queryClient.invalidateQueries(['issues']);
      queryClient.invalidateQueries(['issue']);

      setIsCreating(false);
    },
  });
  
  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => {
      // Remove read-only email metrics fields that should never be updated from UI
      const {
        sentCount,
        openCount,
        clickCount,
        unsubscribeCount,
        bounceCount,
        deliveryRate,
        // Also remove other read-only fields
        created,
        modified,
        authorId,
        authorName,
        issueNumber: _issueNumber, // Remove issueNumber from updates (use id instead)
        ...cleanData
      } = data;

      return issuesApi.update(id, cleanData);
    },
    onSuccess: (updatedIssue) => {
      // Invalidate all issues-related queries to ensure cache consistency
      queryClient.invalidateQueries(['issues']);
      queryClient.invalidateQueries(['issue']);

      // Also update the specific issue in cache optimistically
      if (updatedIssue) {
        queryClient.setQueryData(['issue', 'number', issueNumber], updatedIssue);

        // Update the editing issue state to reflect changes immediately
        setEditingIssue(updatedIssue);
      }
    },
    onError: () => {
      // Error handled by mutation state
    },
  });
  
  const deleteMutation = useMutation({
    mutationFn: issuesApi.delete,
    onSuccess: () => {
      // Invalidate all issues-related queries to ensure cache consistency
      queryClient.invalidateQueries(['issues']);
      queryClient.invalidateQueries(['issue']);
    },
  });

  const refreshStatsMutation = useMutation({
    mutationFn: (issueNumber) => issuesApi.refreshStats(issueNumber),
    onSuccess: (data) => {
      queryClient.setQueryData(['issue', 'number', issueNumber], data);
      setEditingIssue(data);
    },
  });

  const createSingleSendMutation = useMutation({
    mutationFn: (issueNumber) => issuesApi.createSingleSend(issueNumber),
    onSuccess: (data) => {
      queryClient.setQueryData(['issue', 'number', issueNumber], data);
      setEditingIssue(data);
    },
  });

  const updateSingleSendMutation = useMutation({
    mutationFn: (issueNumber) => issuesApi.updateSingleSend(issueNumber),
    onSuccess: (data) => {
      queryClient.setQueryData(['issue', 'number', issueNumber], data);
      setEditingIssue(data);
    },
  });

  // AI Generation handlers
  const handleGenerateNewsSection = async (itemsWithTones) => {
    setIsGeneratingNewsSection(true);
    setNewsSectionError(null);
    setGeneratedNewsBlurbs([]);

    try {
      const result = await generateNewsSection(itemsWithTones);
      setGeneratedNewsBlurbs(result.blurbs || []);
    } catch (err) {
      console.error('Failed to generate news section:', err);
      setNewsSectionError(err.response?.data?.error || err.message || 'Failed to generate news section');
    } finally {
      setIsGeneratingNewsSection(false);
    }
  };

  const handleApplyAllNewsBlurbs = async (blurbs) => {
    // Update each instance with its blurb
    for (const { itemHash, blurb } of blurbs) {
      const instance = assignedItems?.instances?.find((i) => extractItemHash(i.id) === itemHash);
      if (instance) {
        try {
          await updateItemInstance(instance.id, { blurb });
        } catch (err) {
          console.error(`Failed to update blurb for ${itemHash}:`, err);
        }
      }
    }
    // Refresh instances
    queryClient.invalidateQueries(['instances', 'issue', editingIssue?.issueNumber]);
    setShowNewsSectionGenerator(false);
    setGeneratedNewsBlurbs([]);
  };

  const handleApplySingleNewsBlurb = async (itemHash, blurb) => {
    const instance = assignedItems?.instances?.find((i) => extractItemHash(i.id) === itemHash);
    if (instance) {
      try {
        await updateItemInstance(instance.id, { blurb });
        queryClient.invalidateQueries(['instances', 'issue', editingIssue?.issueNumber]);
      } catch (err) {
        console.error(`Failed to update blurb for ${itemHash}:`, err);
      }
    }
  };

  const handleGenerateIssueIntro = async (newsSection, highlightHashes, issueNum) => {
    setIsGeneratingIntro(true);
    setIntroError(null);
    setGeneratedIntro(null);
    setGeneratedTitles([]);

    try {
      const result = await generateIssueIntro(newsSection, highlightHashes, issueNum);
      setGeneratedIntro(result.introLine);
      setGeneratedTitles(result.titleOptions || []);
    } catch (err) {
      console.error('Failed to generate issue intro:', err);
      setIntroError(err.response?.data?.error || err.message || 'Failed to generate intro');
    } finally {
      setIsGeneratingIntro(false);
    }
  };

  const handleSelectIntro = (introLine) => {
    if (editingIssue) {
      updateMutation.mutate({
        id: editingIssue.issueNumber,
        introLine,
      });
    }
  };

  const handleSelectTitle = (title) => {
    if (editingIssue) {
      updateMutation.mutate({
        id: editingIssue.issueNumber,
        title,
      });
    }
  };

  const handleGenerateDeepDiveContent = async (itemHashes, userCommentary, evidenceGuidance, sectionType) => {
    setIsGeneratingDeepDive(true);
    setDeepDiveError(null);
    setGeneratedDeepDive(null);

    try {
      const result = await generateDeepDive(itemHashes, userCommentary, evidenceGuidance, sectionType);
      setGeneratedDeepDive({
        title: result.title,
        paragraphs: result.paragraphs,
        suggestedEdits: result.suggestedEdits,
      });
    } catch (err) {
      console.error('Failed to generate deep dive:', err);
      setDeepDiveError(err.response?.data?.error || err.message || 'Failed to generate deep dive');
    } finally {
      setIsGeneratingDeepDive(false);
    }
  };

  const handleSaveDeepDive = (content) => {
    if (editingIssue) {
      updateMutation.mutate({
        id: editingIssue.issueNumber,
        deepDive: content,
      });
    }
  };

  // Helper function to extract canonical item hash from ItemInstance ID
  const extractItemHash = (instanceId) => {
    // ItemInstance ID format: "hash:ulid", canonical item hash is the part before the colon
    return instanceId.includes(':') ? instanceId.split(':')[0] : instanceId;
  };

  const handleCategoryChange = (itemId, category, position = null) => {
    const updates = { category };
    if (position) {
      updates.position = position;
    }
    // Auto-clear 'new' flag when category is changed (user has acknowledged the item)
    const item = assignedItems?.instances?.find((i) => i.id === itemId);
    if (item?.flag === 'new') {
      updates.flag = null;
    }
    // Skip invalidation - optimistic update is sufficient for inline edits
    updateInstanceFieldMutation.mutate({ instanceId: itemId, updates, skipInvalidation: true });
  };

  const handleFlagChange = (itemId, flag) => {
    // If clicking the same flag, clear it; otherwise set the new flag
    const updates = { flag: flag || null };
    updateInstanceFieldMutation.mutate({ instanceId: itemId, updates, skipInvalidation: true });
  };

  // Mutation for updating instance-specific fields (title, blurb, category, type, url, summary, etc.)
  const updateInstanceFieldMutation = useMutation({
    mutationFn: async ({ instanceId, updates }) => {
      return await updateItemInstance(instanceId, updates);
    },
    onMutate: async ({ instanceId, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['instances', 'issue', editingIssue?.issueNumber]);

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['instances', 'issue', editingIssue?.issueNumber]);

      // Optimistically update the cache
      queryClient.setQueryData(['instances', 'issue', editingIssue?.issueNumber], (oldData) => {
        if (!oldData || !oldData.instances) return oldData;
        return {
          ...oldData,
          instances: oldData.instances.map(item =>
            item.id === instanceId ? { ...item, ...updates } : item
          )
        };
      });

      // Set the item ID that's being updated (for UI feedback)
      setUpdatingItemId(instanceId);

      // Return a context object with the snapshotted value
      return { previousData, instanceId };
    },
    onSuccess: (_, { skipInvalidation }) => {
      // For reorder operations, skip invalidation since optimistic update is sufficient
      if (!skipInvalidation) {
        // Invalidate both the instances query and the issue detail query
        queryClient.invalidateQueries(['instances', 'issue', editingIssue?.issueNumber]);
        queryClient.invalidateQueries(['issue', 'number', issueNumber]);
      }
      setEditingField(null);
      setUpdatingItemId(null);
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(['instances', 'issue', editingIssue?.issueNumber], context.previousData);
      }
      setEditingField(null);
      setUpdatingItemId(null);
    },
  });

  // Mutation for reordering/moving items
  const reorderMutation = useMutation({
    mutationFn: async ({ updates }) => {
      return await reorderInstances(updates);
    },
    onMutate: async ({ updates, optimisticUpdate }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['instances', 'issue', editingIssue?.issueNumber]);

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['instances', 'issue', editingIssue?.issueNumber]);

      // Apply optimistic update if provided
      if (optimisticUpdate && previousData) {
        queryClient.setQueryData(['instances', 'issue', editingIssue?.issueNumber], optimisticUpdate(previousData));
      }

      return { previousData };
    },
    onError: (_, __, context) => {
      // Roll back on error
      if (context?.previousData) {
        queryClient.setQueryData(['instances', 'issue', editingIssue?.issueNumber], context.previousData);
      }
    },
    onSettled: () => {
      // Always refetch after to ensure data consistency
      queryClient.invalidateQueries(['instances', 'issue', editingIssue?.issueNumber]);
    },
  });

  // Handler for reordering items within same category
  const handleItemReorder = async ({ itemId, sourceCategory, targetCategory, newPosition, targetIndex, beforeId, afterId }) => {
    // Update just the position for the item - skip invalidation since optimistic update is sufficient
    updateInstanceFieldMutation.mutate({
      instanceId: itemId,
      updates: {
        position: newPosition,
      },
      skipInvalidation: true,
    });
  };

  // Handler for moving items between categories via drag-and-drop
  const handleDragCategoryChange = async ({ itemId, sourceCategory, targetCategory, newPosition, newCategory }) => {
    // Update both category and position - skip invalidation since optimistic update is sufficient
    updateInstanceFieldMutation.mutate({
      instanceId: itemId,
      updates: {
        category: newCategory === '' ? null : newCategory, // Backend expects null for no category
        position: newPosition,
      },
      skipInvalidation: true,
    });
  };

  const handleFieldDoubleClick = (itemId, fieldName) => {
    setEditingField(`${itemId}-${fieldName}`);
  };

  // Issue field editing handlers
  const handleIssueFieldClick = (fieldName) => {
    setEditingIssueField(fieldName);
  };

  const handleIssueFieldBlur = async (fieldName, value, originalValue) => {
    if (value !== originalValue) {
      try {
        await updateMutation.mutateAsync({
          id: editingIssue.issueNumber,
          [fieldName]: value
        });
      } catch {
        // Error handled by mutation
      }
    }
    setEditingIssueField(null);
  };

  const handleIssueTagsBlur = async (tagsText, originalTags) => {
    const tagsArray = tagsText
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    const originalText = (originalTags || []).join(', ');
    if (tagsText !== originalText) {
      try {
        await updateMutation.mutateAsync({
          id: editingIssue.issueNumber,
          tags: tagsArray
        });
      } catch {
        // Error handled by mutation
      }
    }
    setEditingIssueField(null);
  };

  const handleIssueStatusChange = async (newStatus) => {
    if (newStatus !== editingIssue.status) {
      try {
        await updateMutation.mutateAsync({
          id: editingIssue.issueNumber,
          status: newStatus
        });
      } catch {
        // Error handled by mutation
      }
    }
    setShowStatusDropdown(false);
  };

  const handleFieldBlur = (itemId, fieldName, value, originalValue) => {
    // Only update if value has changed
    if (value !== originalValue) {
      const updates = { [fieldName]: value };

      // On the issues detail page, ALL fields update the ItemInstance
      // (including url, summary, etc.) since we're working with assigned items
      // Skip invalidation - optimistic update is sufficient for inline edits
      updateInstanceFieldMutation.mutate({ instanceId: itemId, updates, skipInvalidation: true });
    } else {
      setEditingField(null);
    }
  };

  const handleViewItemDetails = (itemHash, instanceId) => {
    // Get the list of item hashes for navigation from assigned instances
    // Sort by category order with uncategorized (empty) first, then by position within category
    const categoryOrder = ['', 'news', 'tutorial', 'reads', 'multimedia', 'aws', 'social', 'tools', 'security', 'events'];
    const sortedInstances = [...(assignedItems?.instances || [])].sort((a, b) => {
      const catA = a.category || '';
      const catB = b.category || '';
      const orderA = categoryOrder.indexOf(catA);
      const orderB = categoryOrder.indexOf(catB);
      if (orderA !== orderB) return orderA - orderB;
      // Within same category, sort by position
      return (a.position || '').localeCompare(b.position || '');
    });
    const itemList = sortedInstances.map((instance) => instance.hash);
    const instanceList = sortedInstances.map((instance) => instance.id);
    openSlideOver(itemHash, `/issues/${issueNumber}`, itemList, instanceId, instanceList);
  };

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const expandAll = () => {
    setCollapsedCategories({
      '': false,
      'news': false,
      'tutorial': false,
      'reads': false,
      'multimedia': false,
      'aws': false,
      'social': false,
      'tools': false,
    });
  };

  const collapseAll = () => {
    setCollapsedCategories({
      '': true,
      'news': true,
      'tutorial': true,
      'reads': true,
      'multimedia': true,
      'aws': true,
      'social': true,
      'tools': true,
    });
  };

  const handleSimpleAttributionToggle = async (category) => {
    if (!editingIssue) return;

    // Get current items structure
    const currentItems = editingIssue.items || [];

    // Find the category and toggle its simpleAttribution
    const updatedItems = currentItems.map(item => {
      if (item.category === category) {
        return {
          ...item,
          simpleAttribution: !item.simpleAttribution
        };
      }
      return item;
    });

    // Update the issue
    try {
      await updateMutation.mutateAsync({
        id: editingIssue.issueNumber,
        items: updatedItems
      });
    } catch {
      // Error handled by mutation
    }
  };

  const handleCopyMarkdown = async () => {
    if (!assignedItems?.instances) return;
    
    const markdown = generateMarkdown(assignedItems.instances);
    
    try {
      await navigator.clipboard.writeText(markdown);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = markdown;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch {
        // Copy failed silently
      }
      document.body.removeChild(textArea);
    }
  };

  // Group items by category in the specified order
  const groupItemsByCategory = (items) => {
    if (!items) return {};

    const categoryOrder = ['', 'news', 'tutorial', 'reads', 'multimedia', 'aws', 'social', 'tools', 'security', 'events'];
    const grouped = {};

    // Initialize all categories
    categoryOrder.forEach(category => {
      grouped[category] = [];
    });

    // Group items by category and sort by position within each category
    items.forEach(item => {
      const category = item.category || '';
      if (grouped[category]) {
        grouped[category].push(item);
      } else {
        grouped[''].push(item); // Default to "No Category" for unknown categories
      }
    });

    // Sort items within each category by position
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) =>
        (a.position || 'm00000').localeCompare(b.position || 'm00000')
      );
    });

    return grouped;
  };

  // Filter instances by flag (client-side filtering)
  const filteredInstances = React.useMemo(() => {
    if (!assignedItems?.instances) return [];
    if (!flagFilter) return assignedItems.instances;
    return assignedItems.instances.filter(item => item.flag === flagFilter);
  }, [assignedItems?.instances, flagFilter]);

  // Memoized grouped items for drag-and-drop
  const groupedItemsForDnd = React.useMemo(() => {
    if (!filteredInstances.length) return {};
    return groupItemsByCategory(filteredInstances);
  }, [filteredInstances]);

  // Sortable articles hook for drag-and-drop
  // The hook returns groupedItems that reflects local state during drag
  const {
    activeItem: dndActiveItem,
    activeWidth: dndActiveWidth,
    activeCategory: dndActiveCategory,
    overCategory: dndOverCategory,
    isDragging: dndIsDragging,
    groupedItems: dndGroupedItems,
    dndContextProps,
  } = useSortableArticles({
    groupedItems: groupedItemsForDnd,
    onReorder: handleItemReorder,
    onCategoryChange: handleDragCategoryChange,
  });

  const typeOptions = [
    { value: 'article', label: 'Article', icon: DocumentTextIcon },
    { value: 'video', label: 'Video', icon: VideoCameraIcon },
    { value: 'audio', label: 'Audio', icon: SpeakerWaveIcon },
    { value: 'image', label: 'Image', icon: PhotoIcon },
    { value: 'social', label: 'Social', icon: CursorArrowRaysIcon },
    { value: 'unknown', label: 'Unknown', icon: QuestionMarkCircleIcon },
  ];

  const getTypeIcon = (type) => {
    const typeOption = typeOptions.find(option => option.value === type);
    return typeOption ? typeOption.icon : QuestionMarkCircleIcon;
  };

  const getCategoryDisplayName = (category, isMarkdown = false) => {
    if (isMarkdown) {
      switch (category) {
        case '': return 'No Category';
        case 'news': return 'News & Announcements';
        case 'tutorial': return 'Tutorials';
        case 'reads': return 'Reads';
        case 'multimedia': return 'Podcasts, Videos, and more';
        case 'aws': return 'New from AWS';
        case 'social': return 'Thoughts from Social';
        case 'tools': return 'Developer Tools';
        case 'security': return 'Security';
        case 'events': return 'Events';
        default: return 'Other';
      }
    } else {
      switch (category) {
        case '': return 'No Category';
        case 'news': return 'News';
        case 'tutorial': return 'Tutorial';
        case 'reads': return 'Reads';
        case 'multimedia': return 'Multimedia';
        case 'aws': return 'AWS';
        case 'social': return 'Social';
        case 'tools': return 'Tools';
        case 'security': return 'Security';
        case 'events': return 'Events';
        default: return 'Other';
      }
    }
  };

  // Generate markdown representation of assigned items
  const generateMarkdown = (items) => {
    if (!items || items.length === 0) {
      return 'No items have been assigned to this issue yet.';
    }

    // Get the current issue number for UTM tracking
    const currentIssueNumber = editingIssue?.issueNumber || '';
    const utmParams = `?utm_source=newsletter&utm_medium=email&utm_content=offbynone&utm_campaign=Off-by-none%3A%20Issue%20%23${currentIssueNumber}`;

    let markdown = '';

    // Use the saved order from editingIssue.items
    const savedOrder = editingIssue?.items || [];

    // If no saved order, fall back to grouping by category
    if (savedOrder.length === 0) {
      const groupedItems = groupItemsByCategory(items);
      const categoryOrder = ['', 'news', 'tutorial', 'reads', 'multimedia', 'aws', 'social', 'tools', 'security', 'events'];

      categoryOrder.forEach(category => {
        const categoryItems = groupedItems[category];
        if (!categoryItems || categoryItems.length === 0) return;

        const categoryName = getCategoryDisplayName(category, true);
        markdown += `### ${categoryName}\n\n`;

        categoryItems.forEach((item) => {
          markdown += formatItemMarkdown(item, category, utmParams, false);
        });

        markdown += '\n';
      });
    } else {
      // Use the saved order
      savedOrder.forEach(({ category, items: itemIds, simpleAttribution }) => {
        if (!itemIds || itemIds.length === 0) return;

        const categoryName = getCategoryDisplayName(category, true);
        markdown += `### ${categoryName}\n\n`;

        // Get items in the saved order
        itemIds.forEach(itemId => {
          const item = items.find(i => i.id === itemId);
          if (item) {
            markdown += formatItemMarkdown(item, category, utmParams, simpleAttribution || false);
          }
        });

        markdown += '\n';
      });
    }

    return markdown.trim();
  };

  // Helper function to format a single item's markdown
  const formatItemMarkdown = (item, category, utmParams, simpleAttribution = false) => {
    let markdown = '';

    // Add UTM tracking parameters to the URL
    const trackedUrl = item.url + utmParams;

    // Helper function to format authors
    const formatAuthors = (authors) => {
      if (!authors || authors.length === 0) return '';

      if (authors.length === 1) {
        return authors[0];
      } else if (authors.length === 2) {
        return `${authors[0]} and ${authors[1]}`;
      } else {
        // 3 or more authors: use Oxford comma
        const allButLast = authors.slice(0, -1);
        const lastAuthor = authors[authors.length - 1];
        return `${allButLast.join(', ')}, and ${lastAuthor}`;
      }
    };

    // Category-specific formatting
    if (category === 'news') {
      // News & Announcements: linked title, blurb on next line, extra line break between items
      markdown += `[${item.title}](${trackedUrl})`;
      if (item.blurb && item.blurb.trim()) {
        markdown += `  \n${item.blurb}`;
      }
      markdown += '\n\n';
    } else if (category === 'social' || category === 'security') {
      // Social, Security: No bullets, no authors, extra line break between items
      markdown += `[${item.title}](${trackedUrl})\n\n`;
    } else if (category === 'aws') {
      // AWS: Bullets with no attribution
      markdown += `* [${item.title}](${trackedUrl})\n`;
    } else if (simpleAttribution && category !== 'reads' && category !== 'multimedia') {
      // Simple attribution: bullets with "by {author}" on same line as title (except reads/multimedia which handle their own)
      markdown += `* [${item.title}](${trackedUrl})`;
      if (item.authors && Array.isArray(item.authors) && item.authors.length > 0) {
        const authorText = formatAuthors(item.authors);
        markdown += ` by ${authorText}`;
      }
      markdown += '\n';
    } else if (category === 'tutorial') {
      // Tutorials: Keep bullets, hide "by" if author array is empty
      markdown += `* [${item.title}](${trackedUrl})`;
      if (item.authors && Array.isArray(item.authors) && item.authors.length > 0) {
        const authorText = formatAuthors(item.authors);
        markdown += ` by ${authorText}`;
      }
      markdown += '\n';
    } else if (category === 'reads') {
      // Reads: Different formatting based on simpleAttribution
      if (simpleAttribution) {
        // Simple attribution: No bullets, extra line space, author on separate line without "by"
        markdown += `[${item.title}](${trackedUrl})\n`;
        if (item.authors && Array.isArray(item.authors) && item.authors.length > 0) {
          const authorText = formatAuthors(item.authors);
          markdown += `${authorText}\n`;
        }
        markdown += '\n';
      } else {
        // Full attribution: Linked title with author on same line, then blurb on next line
        markdown += `[${item.title}](${trackedUrl})`;
        if (item.authors && Array.isArray(item.authors) && item.authors.length > 0) {
          const authorText = formatAuthors(item.authors);
          markdown += ` by ${authorText}`;
        }
        markdown += '  \n';
        if (item.blurb && item.blurb.trim()) {
          markdown += `${item.blurb}\n`;
        }
        markdown += '\n';
      }
    } else if (category === 'multimedia') {
      // Multimedia (Podcasts, Videos): Different formatting based on simpleAttribution
      if (simpleAttribution) {
        // Simple attribution: No bullets, extra line space, "with {author}" on separate line
        markdown += `[${item.title}](${trackedUrl})\n`;
        if (item.authors && Array.isArray(item.authors) && item.authors.length > 0) {
          const authorText = formatAuthors(item.authors);
          markdown += `with ${authorText}\n`;
        }
        markdown += '\n';
      } else {
        // Full attribution: Linked title, then blurb on next line
        markdown += `[${item.title}](${trackedUrl})  \n`;
        if (item.blurb && item.blurb.trim()) {
          markdown += `${item.blurb}\n`;
        }
        markdown += '\n';
      }
    } else if (category === 'tools') {
      // Tools: No bullets, optional author on same line with "by", blurb on line after
      markdown += `[${item.title}](${trackedUrl})`;
      if (item.authors && Array.isArray(item.authors) && item.authors.length > 0) {
        const authorText = formatAuthors(item.authors);
        if (authorText && authorText.trim()) {
          markdown += ` by ${authorText}`;
        }
      }
      markdown += '  \n';
      if (item.blurb && item.blurb.trim()) {
        markdown += `${item.blurb}\n`;
      }
      markdown += '\n';
    } else if (category === 'events') {
      // Events: Format as "*Month Day, Year* - [Event name](url)"
      if (item.published) {
        const eventDate = new Date(item.published);
        const formattedDate = eventDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        markdown += `*${formattedDate}* - [${item.title}](${trackedUrl})\n\n`;
      } else {
        // Fallback if no published date
        markdown += `[${item.title}](${trackedUrl})\n\n`;
      }
    } else {
      // Default fallback
      markdown += `[${item.title}](${trackedUrl})\n\n`;
    }

    return markdown;
  };

  const handleCancel = () => {
    // Navigate back to issues list
    setEditingIssue(null);
    if (issueNumber) {
      navigate('/issues');
    }
  };

  const handleCreateIssue = async (e) => {
    e.preventDefault();

    if (!user) {
      return;
    }

    const issueNum = newIssueNumber ? parseInt(newIssueNumber) : undefined;
    const title = issueNum ? `Issue #${issueNum}` : 'New Newsletter Issue';

    try {
      await createMutation.mutateAsync({
        issueNumber: issueNum,
        title: title,
        description: title,
        subject: 'Off-by-none #{issueNumber}: {issueTitle}',
        status: 'unpublished',
        authorId: user.id,
        authorName: user.name || user.email,
        ...(newIssueDate && { issueDate: newIssueDate }),
      });
      setNewIssueNumber('');
      setNewIssueDate('');
      setIsCreating(false);
    } catch {
      // Error handled by mutation
    }
  };

  
  if (isLoading || (issueNumber && isLoadingDeepLinkedIssue)) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 dark:text-gray-400">
          {issueNumber ? `Loading issue #${issueNumber}...` : 'Loading issues...'}
        </div>
      </div>
    );
  }

  // Handle case where deep-linked issue is not found
  if (issueNumber && !isLoadingDeepLinkedIssue && !deepLinkedIssue && !editingIssue) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <XCircleIcon className="h-16 w-16 text-red-400" />
          </div>
          <h2 className="text-lg font-medium text-obn-blue dark:text-white mb-2">Issue Not Found</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">Issue #{issueNumber} could not be found.</p>
          <button
            onClick={() => navigate('/issues')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
          >
            ← Back to Issues
          </button>
        </div>
      </div>
    );
  }

  // When viewing/editing an issue
  if (editingIssue) {
    return (
      <>
      <div className="px-0 sm:px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6 px-4 sm:px-0">
          <div>
            <h1 className="text-2xl font-bold text-obn-blue dark:text-white">
              Issue #{editingIssue.issueNumber}
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{editingIssue.title}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCancel}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
            >
              ← Back to Issues
            </button>
          </div>
        </div>
        
        {/* Email Stats - Published issues only */}
        {/* Email Campaign Panel */}
        <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 sm:shadow sm:rounded-lg p-6 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
              <EnvelopeIcon className="h-4 w-4" />
              Email Campaign
            </h3>
            {editingIssue.singleSendId && (
              <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{editingIssue.singleSendId}</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowEmailPreview(!showEmailPreview)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <EyeIcon className="h-3.5 w-3.5" />
              {showEmailPreview ? 'Hide Preview' : 'Preview Email'}
            </button>

            {!editingIssue.singleSendId ? (
              <button
                onClick={() => createSingleSendMutation.mutate(editingIssue.issueNumber)}
                disabled={!editingIssue.subject || createSingleSendMutation.isPending}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-obn-blue rounded-md hover:bg-obn-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!editingIssue.subject ? 'Set an email subject first' : 'Create Single Send in SendGrid'}
              >
                {createSingleSendMutation.isPending ? (
                  <>
                    <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Single Send'
                )}
              </button>
            ) : (
              <button
                onClick={() => updateSingleSendMutation.mutate(editingIssue.issueNumber)}
                disabled={updateSingleSendMutation.isPending}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-obn-blue rounded-md hover:bg-obn-blue/90 transition-colors disabled:opacity-50"
                title="Re-sync content to SendGrid"
              >
                {updateSingleSendMutation.isPending ? (
                  <>
                    <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                    Update Single Send
                  </>
                )}
              </button>
            )}

            <button
              disabled
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-md cursor-not-allowed"
              title="Publishing from the admin UI is not yet supported"
            >
              Publish
            </button>
          </div>

          {!editingIssue.subject && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Set an email subject to enable Single Send creation.</p>
          )}

          {createSingleSendMutation.isError && (
            <p className="mt-2 text-xs text-red-600 dark:text-red-400">
              Error: {createSingleSendMutation.error?.response?.data?.error || createSingleSendMutation.error?.message}
            </p>
          )}
          {updateSingleSendMutation.isError && (
            <p className="mt-2 text-xs text-red-600 dark:text-red-400">
              Error: {updateSingleSendMutation.error?.response?.data?.error || updateSingleSendMutation.error?.message}
            </p>
          )}

          {showEmailPreview && (
            <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
              <EmailPreviewIframe issue={editingIssue} />
            </div>
          )}
        </div>

        {editingIssue.status === 'published' && (
          <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 sm:shadow sm:rounded-lg p-6 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">Email Stats</h3>
              <button
                onClick={() => refreshStatsMutation.mutate(editingIssue.issueNumber)}
                disabled={refreshStatsMutation.isPending}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-obn-blue dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                title="Refresh stats from SendGrid"
              >
                <ArrowPathIcon className={`h-3.5 w-3.5 ${refreshStatsMutation.isPending ? 'animate-spin' : ''}`} />
                {refreshStatsMutation.isPending ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            {editingIssue.sentCount > 0 ? (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{(editingIssue.sentCount || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Sent</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{(editingIssue.deliveredCount || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Delivered</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-green-600 dark:text-green-400">{(editingIssue.uniqueOpenCount || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Unique Opens</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-obn-blue dark:text-blue-400">{(editingIssue.uniqueClickCount || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Unique Clicks</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{(editingIssue.bounceCount || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Bounces</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{(editingIssue.unsubscribeCount || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Unsubs</div>
                  </div>
                </div>
                {editingIssue.deliveredCount > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 grid grid-cols-3 sm:grid-cols-6 gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <div className="text-center">
                      {editingIssue.deliveryRate > 0 ? `${editingIssue.deliveryRate.toFixed(1)}%` : `${((editingIssue.deliveredCount / editingIssue.sentCount) * 100).toFixed(1)}%`} delivery
                    </div>
                    <div className="text-center" />
                    <div className="text-center">
                      {((editingIssue.uniqueOpenCount / editingIssue.deliveredCount) * 100).toFixed(1)}% open rate
                    </div>
                    <div className="text-center">
                      {((editingIssue.uniqueClickCount / editingIssue.deliveredCount) * 100).toFixed(1)}% click rate
                    </div>
                    <div className="text-center">
                      {(editingIssue.bounceDropCount || 0) + (editingIssue.invalidEmailCount || 0) > 0 && (
                        <span>+{((editingIssue.bounceDropCount || 0) + (editingIssue.invalidEmailCount || 0)).toLocaleString()} dropped</span>
                      )}
                    </div>
                    <div className="text-center">
                      {(editingIssue.spamReportCount || 0) > 0 && (
                        <span>{editingIssue.spamReportCount} spam</span>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">No stats yet. Click Refresh to fetch from SendGrid.</p>
            )}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 sm:shadow sm:rounded-lg p-6">
          <div className="space-y-6">
            {/* Status - Click to Change */}
            <div className="status-dropdown">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Status</h3>
              <div className="relative inline-block">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                    editingIssue.status === 'published' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
                  }`}
                >
                  {editingIssue.status === 'published' ? 'Published' : 'Unpublished'}
                  <ChevronDownIcon className="ml-1 h-4 w-4" />
                </button>

                {showStatusDropdown && (
                  <div className="absolute z-10 mt-1 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700">
                    <div className="py-1">
                      <button
                        onClick={() => handleIssueStatusChange('published')}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          editingIssue.status === 'published' ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200 font-medium' : 'text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        Published
                      </button>
                      <button
                        onClick={() => handleIssueStatusChange('unpublished')}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          editingIssue.status === 'unpublished' ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 font-medium' : 'text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        Unpublished
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Title - Click to Edit */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Title</h3>
              {editingIssueField === 'title' ? (
                <input
                  type="text"
                  defaultValue={editingIssue.title}
                  autoFocus
                  onBlur={(e) => handleIssueFieldBlur('title', e.target.value, editingIssue.title)}
                  className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 text-lg text-obn-blue dark:text-white px-3 py-2"
                />
              ) : (
                <p
                  onClick={() => handleIssueFieldClick('title')}
                  className="text-lg text-obn-blue dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded -ml-2"
                >
                  {editingIssue.title}
                </p>
              )}
            </div>

            {/* Description - Click to Edit */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Description</h3>
              {editingIssueField === 'description' ? (
                <textarea
                  defaultValue={editingIssue.description}
                  rows={2}
                  autoFocus
                  onBlur={(e) => handleIssueFieldBlur('description', e.target.value, editingIssue.description)}
                  className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm px-3 py-2"
                />
              ) : (
                <p
                  onClick={() => handleIssueFieldClick('description')}
                  className="text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded -ml-2"
                >
                  {editingIssue.description}
                </p>
              )}
            </div>

            {/* Issue Date, Email Subject, Preview Text - Click to Edit */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Issue Date</h3>
                {editingIssueField === 'issueDate' ? (
                  <input
                    type="date"
                    defaultValue={editingIssue.issueDate || ''}
                    autoFocus
                    onBlur={(e) => handleIssueFieldBlur('issueDate', e.target.value, editingIssue.issueDate)}
                    className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm px-3 py-2"
                  />
                ) : (
                  <p
                    onClick={() => handleIssueFieldClick('issueDate')}
                    className="text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded -ml-2"
                  >
                    {editingIssue.issueDate
                      ? new Date(editingIssue.issueDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
                      : <span className="text-gray-400 dark:text-gray-500 italic">Set issue date</span>}
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Email Subject</h3>
                {editingIssueField === 'subject' ? (
                  <input
                    type="text"
                    defaultValue={editingIssue.subject || ''}
                    autoFocus
                    onBlur={(e) => handleIssueFieldBlur('subject', e.target.value, editingIssue.subject)}
                    className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm px-3 py-2"
                    placeholder="📰 Your Weekly Tech Update"
                  />
                ) : (
                  <p
                    onClick={() => handleIssueFieldClick('subject')}
                    className="text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded -ml-2"
                  >
                    {editingIssue.subject || <span className="text-gray-400 dark:text-gray-500 italic">Add subject</span>}
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Preview Text</h3>
                {editingIssueField === 'previewText' ? (
                  <input
                    type="text"
                    defaultValue={editingIssue.previewText || ''}
                    autoFocus
                    onBlur={(e) => handleIssueFieldBlur('previewText', e.target.value, editingIssue.previewText)}
                    className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm px-3 py-2"
                    placeholder="Email preview/preheader text"
                  />
                ) : (
                  <p
                    onClick={() => handleIssueFieldClick('previewText')}
                    className="text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded -ml-2"
                  >
                    {editingIssue.previewText || <span className="text-gray-400 dark:text-gray-500 italic">Add preview text</span>}
                  </p>
                )}
              </div>
            </div>

            {/* Tags - Click to Edit */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Tags (comma separated)</h3>
              {editingIssueField === 'tags' ? (
                <input
                  type="text"
                  defaultValue={(editingIssue.tags || []).join(', ')}
                  autoFocus
                  onBlur={(e) => handleIssueTagsBlur(e.target.value, editingIssue.tags)}
                  className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm px-3 py-2"
                  placeholder="tech, startups, AI"
                />
              ) : (
                <div
                  onClick={() => handleIssueFieldClick('tags')}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded -ml-2"
                >
                  {editingIssue.tags && editingIssue.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {editingIssue.tags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-obn-lightblue text-obn-blue dark:bg-blue-900/40 dark:text-blue-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 italic">Add tags</span>
                  )}
                </div>
              )}
            </div>

            {/* Notes - Click to Edit */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Internal Notes</h3>
              {editingIssueField === 'notes' ? (
                <textarea
                  defaultValue={editingIssue.notes || ''}
                  rows={2}
                  autoFocus
                  onBlur={(e) => handleIssueFieldBlur('notes', e.target.value, editingIssue.notes)}
                  className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm px-3 py-2"
                  placeholder="Internal notes about the issue"
                />
              ) : (
                <p
                  onClick={() => handleIssueFieldClick('notes')}
                  className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-900 p-3 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {editingIssue.notes || <span className="text-gray-400 dark:text-gray-500 italic">Add notes</span>}
                </p>
              )}
            </div>

            {/* Content - Click to toggle between view/edit */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Newsletter Content</h3>
              {editingIssueField === 'content' ? (
                <textarea
                  defaultValue={editingIssue.content || ''}
                  autoFocus
                  onBlur={(e) => handleIssueFieldBlur('content', e.target.value, editingIssue.content)}
                  onFocus={(e) => {
                    // Auto-resize on focus to fit existing content
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  onInput={(e) => {
                    // Auto-resize textarea to fit content as user types
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  style={{ minHeight: '150px' }}
                  className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm font-mono text-sm resize-none overflow-hidden px-3 py-2"
                  placeholder="# Welcome to Issue #42&#10;&#10;This week we cover..."
                />
              ) : (
                <div
                  onClick={() => setEditingIssueField('content')}
                  className="min-h-[150px] p-4 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {editingIssue.content ? (
                    <>
                      <div className="relative bg-white dark:bg-gray-900 rounded-md">
                        <div
                          ref={contentPreviewRef}
                          className={`prose prose-sm max-w-[600px] mx-auto px-10 py-8 dark:prose-invert prose-headings:text-obn-blue dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-200 prose-a:text-obn-blue dark:prose-a:text-blue-400 prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-code:text-gray-800 dark:prose-code:text-gray-200 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 ${
                            contentOverflows && !contentExpanded ? 'max-h-[400px] overflow-hidden' : ''
                          }`}
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {editingIssue.content}
                          </ReactMarkdown>
                        </div>
                        {contentOverflows && !contentExpanded && (
                          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white dark:from-gray-900 to-transparent rounded-b-md" />
                        )}
                      </div>
                      {contentOverflows && (
                        <div className="relative z-10 flex justify-center mt-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setContentExpanded(!contentExpanded);
                            }}
                            className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            {contentExpanded ? 'Show less' : 'Show all'}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-400 dark:text-gray-500 italic">Click to add content</p>
                  )}
                </div>
              )}
            </div>

            {/* Issue Intro Generator */}
            <IssueIntroGenerator
              issueNumber={editingIssue?.issueNumber}
              availableItems={(assignedItems?.instances || []).map((inst) => ({
                hash: extractItemHash(inst.id),
                title: inst.title,
                summary: inst.summary,
              }))}
              newsSection={editingIssue?.content || ''}
              onIntroSelect={handleSelectIntro}
              onTitleSelect={handleSelectTitle}
              onGenerate={handleGenerateIssueIntro}
              isGenerating={isGeneratingIntro}
              generatedIntro={generatedIntro}
              generatedTitles={generatedTitles}
              error={introError}
            />

            {/* Deep Dive Section */}
            <DeepDiveSection
              issueNumber={editingIssue?.issueNumber}
              availableItems={(assignedItems?.instances || []).map((inst) => ({
                hash: extractItemHash(inst.id),
                title: inst.title,
                summary: inst.summary,
              }))}
              deepDiveContent={editingIssue?.deepDive}
              onContentSave={handleSaveDeepDive}
              onGenerate={handleGenerateDeepDiveContent}
              isGenerating={isGeneratingDeepDive}
              generatedContent={generatedDeepDive}
              error={deepDiveError}
            />

            {/* Assigned Items Section */}
            <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">Assigned Items</h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {assignedItems?.instances
                        ? flagFilter
                          ? `${filteredInstances.length} of ${assignedItems.instances.length} items`
                          : `${assignedItems.instances.length} items`
                        : '0 items'}
                    </span>
                    {/* Expand/Collapse All buttons */}
                    {assignedItemsTab === 'edit' && assignedItems?.instances && assignedItems.instances.length > 0 && (
                      <div className="flex space-x-1">
                        <button
                          onClick={expandAll}
                          title="Expand all categories (E)"
                          className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Expand All (E)
                        </button>
                        <button
                          onClick={collapseAll}
                          title="Collapse all categories (C)"
                          className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Collapse All (C)
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Tab Navigation */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setAssignedItemsTab('edit')}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        assignedItemsTab === 'edit'
                          ? 'bg-obn-lightblue text-obn-blue dark:bg-blue-900/40 dark:text-blue-200 border border-obn-blue/20 dark:border-blue-400/30'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setAssignedItemsTab('markdown')}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        assignedItemsTab === 'markdown'
                          ? 'bg-primary-100 text-primary-700 dark:bg-blue-900/40 dark:text-blue-200 border border-primary-200 dark:border-blue-400/30'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      Markdown
                    </button>
                  </div>

                  {/* Flag Filter */}
                  {assignedItemsTab === 'edit' && assignedItems?.instances?.length > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Filter:</span>
                      <button
                        onClick={() => setFlagFilter(null)}
                        title="Show all (A)"
                        className={`px-2 py-1 text-xs font-medium border rounded transition-all ${
                          flagFilter === null
                            ? 'bg-gray-200 text-gray-700 border-gray-400 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-500'
                            : 'bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700'
                        }`}
                      >
                        All (A)
                      </button>
                      {INSTANCE_FLAGS.map((flagOpt) => {
                        const count = assignedItems.instances.filter(i => i.flag === flagOpt.value).length;
                        const shortcut = FLAG_FILTER_SHORTCUTS[flagOpt.value];
                        return (
                          <button
                            key={flagOpt.value}
                            onClick={() => {
                              const newFilter = flagFilter === flagOpt.value ? null : flagOpt.value;
                              setFlagFilter(newFilter);
                              if (newFilter) expandAll();
                            }}
                            className={`px-2 py-1 text-xs font-medium border rounded flex items-center gap-1 transition-all ${
                              flagFilter === flagOpt.value
                                ? `${flagOpt.bgColor} ${flagOpt.color} ${flagOpt.borderColor}`
                                : `bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700`
                            }`}
                            title={`Filter by ${flagOpt.label} (${shortcut}) — ${count} items`}
                          >
                            {flagFilter === flagOpt.value ? (
                              <FlagIconSolid className={`h-3 w-3 ${flagOpt.color}`} />
                            ) : (
                              <FlagIcon className="h-3 w-3" />
                            )}
                            {flagOpt.label} ({shortcut})
                            {count > 0 && (
                              <span className={`ml-0.5 text-[10px] ${flagFilter === flagOpt.value ? '' : 'text-gray-400 dark:text-gray-500'}`}>
                                ({count})
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {isLoadingItems ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">Loading items...</div>
                ) : assignedItems?.instances && assignedItems.instances.length > 0 ? (
                  <>
                    {assignedItemsTab === 'edit' ? (
                      <DndContext {...dndContextProps}>
                        <div className="sm:border sm:border-gray-200 dark:sm:border-gray-700 sm:rounded-md sm:p-3">
                          {(() => {
                          // Use dndGroupedItems which reflects local state during drag
                          const categoryOrder = ['', 'news', 'tutorial', 'reads', 'multimedia', 'aws', 'social', 'tools', 'security', 'events'];

                          return categoryOrder.map(category => {
                            const items = dndGroupedItems[category] || [];
                            const isEmpty = items.length === 0;

                            // Hide empty categories unless dragging
                            if (isEmpty && !dndIsDragging) return null;

                            return (
                              <div key={category} className={`mb-6 last:mb-0 ${isEmpty ? 'opacity-60' : ''}`}>
                                <div className="w-full flex items-center justify-between text-base font-semibold text-blue-800 dark:text-blue-300 mb-3 pb-1 border-b border-gray-200 dark:border-gray-700">
                                  <button
                                    onClick={() => !isEmpty && toggleCategory(category)}
                                    className={`flex items-center flex-1 px-2 py-1 rounded-t transition-colors ${isEmpty ? 'cursor-default' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                  >
                                    <span>{getCategoryDisplayName(category)} ({items.length})</span>
                                    {!isEmpty && (
                                      <ChevronDownIcon
                                        className={`h-5 w-5 ml-2 transition-transform duration-200 ${
                                          collapsedCategories[category] ? '-rotate-90' : ''
                                        }`}
                                      />
                                    )}
                                  </button>
                                  {!isEmpty && category === 'news' && (
                                    <button
                                      onClick={() => setShowNewsSectionGenerator(true)}
                                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 dark:text-indigo-200 dark:bg-indigo-900/30 dark:border-indigo-800/50 dark:hover:bg-indigo-900/50"
                                    >
                                      <SparklesIcon className="h-3.5 w-3.5" />
                                      Generate Section
                                    </button>
                                  )}
                                </div>
                                {!collapsedCategories[category] && (
                                  <SortableCategory
                                    category={category}
                                    itemIds={items.map(i => i.id)}
                                  >
                                    {({ isOver }) => (
                                      <div className={`space-y-3 min-h-[40px] ${isOver && dndActiveCategory !== category ? 'bg-blue-50 dark:bg-blue-900/30 rounded-lg p-2 -m-2' : ''}`}>
                                      {isEmpty ? (
                                        <div className={`border-2 border-dashed rounded-lg p-4 text-center text-sm text-gray-400 dark:text-gray-500 ${isOver ? 'border-obn-blue bg-blue-50 dark:bg-blue-900/30' : 'border-gray-300 dark:border-gray-600'}`}>
                                          Drop items here
                                        </div>
                                      ) : items.map((item) => (
                                      <SortableItem key={item.id} id={item.id} category={category} overCategory={dndOverCategory}>
                                        {({ ref, style, attributes, listeners, isDragging }) => (
                                          <div
                                            ref={ref}
                                            style={style}
                                            className={`draggable-item border border-obn-lightblue dark:border-blue-900/40 dark:bg-gray-800 rounded mb-3 last:mb-0 flex ${
                                              isDragging ? 'opacity-50 shadow-lg z-50' : ''
                                            }`}
                                          >
                                            {/* Drag Handle - stretches full height, colored by flag */}
                                            <div
                                              className={`drag-handle relative w-10 flex flex-col items-center rounded-l flex-shrink-0 self-stretch transition-colors ${
                                                item.flag === 'review' ? 'bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/40 dark:hover:bg-orange-900/60' :
                                                item.flag === 'followup' ? 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/40 dark:hover:bg-purple-900/60' :
                                                item.flag === 'new' ? 'bg-green-100 hover:bg-green-200 dark:bg-green-900/40 dark:hover:bg-green-900/60' :
                                                item.position ? 'bg-obn-lightblue hover:bg-blue-200 dark:bg-blue-900/40 dark:hover:bg-blue-900/60' : 'bg-red-100 hover:bg-red-200 dark:bg-red-900/40 dark:hover:bg-red-900/60'
                                              }`}
                                            >
                                              {/* Flag Icon - at top, aligned with title */}
                                              <div
                                                className="flag-selector relative z-10 pt-3 w-full flex justify-center"
                                                onMouseEnter={() => setHoveredFlagItem(item.id)}
                                                onMouseLeave={() => setHoveredFlagItem(null)}
                                              >
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setHoveredFlagItem(hoveredFlagItem === item.id ? null : item.id);
                                                  }}
                                                  disabled={updatingItemId === item.id}
                                                  className={`p-1 rounded transition-colors ${
                                                    item.flag
                                                      ? `${getFlagOption(item.flag)?.color || 'text-gray-400 dark:text-gray-500'}`
                                                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                                                  } disabled:opacity-50`}
                                                  title={item.flag ? `Flag: ${getFlagOption(item.flag)?.label || item.flag}` : 'Set flag'}
                                                >
                                                  {item.flag ? (
                                                    <FlagIconSolid className="h-4 w-4" />
                                                  ) : (
                                                    <FlagIcon className="h-4 w-4" />
                                                  )}
                                                </button>
                                                {/* Expanded state on hover */}
                                                {hoveredFlagItem === item.id && (
                                                  <div
                                                    className="absolute z-20 flex items-center gap-1 bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 shadow-lg rounded-md p-1 border border-gray-200 dark:border-gray-700"
                                                    style={{ animation: 'slideIn 150ms ease-out', left: '-9px', top: '-2px' }}
                                                  >
                                                    {INSTANCE_FLAGS.map((flagOpt) => (
                                                      <button
                                                        key={flagOpt.value}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleFlagChange(
                                                            item.id,
                                                            item.flag === flagOpt.value ? null : flagOpt.value
                                                          );
                                                          setHoveredFlagItem(null);
                                                        }}
                                                        disabled={updatingItemId === item.id}
                                                        className={`px-2 py-1 text-xs font-medium border rounded flex items-center gap-1 transition-all whitespace-nowrap ${
                                                          item.flag === flagOpt.value
                                                            ? `${flagOpt.bgColor} ${flagOpt.color} ${flagOpt.borderColor} ring-2 ring-offset-1 ring-gray-400 dark:ring-offset-gray-800`
                                                            : `bg-gray-50 text-gray-600 border-gray-300 ${flagOpt.hoverColor} dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600`
                                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                        title={item.flag === flagOpt.value ? `Clear ${flagOpt.label} flag` : `Set ${flagOpt.label} flag`}
                                                      >
                                                        {item.flag === flagOpt.value ? (
                                                          <FlagIconSolid className={`h-3 w-3 ${flagOpt.color}`} />
                                                        ) : (
                                                          <FlagIcon className="h-3 w-3" />
                                                        )}
                                                        {flagOpt.label}
                                                      </button>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                              {/* Drag Grip - absolutely centered in the full item height */}
                                              <div
                                                {...listeners}
                                                {...attributes}
                                                className="absolute inset-0 flex items-center justify-center cursor-move"
                                                title={item.position ? 'Drag to reorder' : 'No position assigned - drag to set position'}
                                              >
                                                <svg className={`w-4 h-4 ${
                                                  item.flag === 'review' ? 'text-orange-600' :
                                                  item.flag === 'followup' ? 'text-purple-600' :
                                                  item.flag === 'new' ? 'text-blue-600' :
                                                  item.position ? 'text-obn-blue' : 'text-obn-red'
                                                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                                </svg>
                                              </div>
                                            </div>

                                            {/* Content wrapper - contains both the header row and expanded content */}
                                            <div
                                              className="flex-1 flex flex-col min-w-0"
                                              onDoubleClick={(e) => {
                                                // Only trigger if not clicking on an interactive element
                                                const target = e.target;
                                                const isInteractive = target.closest('button, a, input, textarea, [role="button"]');
                                                if (!isInteractive) {
                                                  handleViewItemDetails(item.hash, item.id);
                                                }
                                              }}
                                            >
                                      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start pl-3 pr-3 py-3">
                                        <div className="flex-1 mb-2 lg:mb-0 min-w-0">
                                          {/* Title */}
                                          <div className="mb-1 flex items-start">
                                            {/* Type Icon (read-only, edit in item details) */}
                                            <div className="mr-2 p-1" title={`Type: ${item.type || 'unknown'} | Edit in item details panel`}>
                                              {(() => {
                                                const IconComponent = getTypeIcon(item.type);
                                                return <IconComponent className="h-4 w-4 text-gray-500 dark:text-gray-400" />;
                                              })()}
                                            </div>
                                            {editingField === `${item.id}-title` ? (
                                              <input
                                                type="text"
                                                defaultValue={item.title}
                                                onBlur={(e) => handleFieldBlur(item.id, 'title', e.target.value, item.title)}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    e.target.blur();
                                                  }
                                                  if (e.key === 'Escape') {
                                                    setEditingField(null);
                                                  }
                                                }}
                                                className="text-sm font-medium text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-primary-300 dark:border-gray-600 rounded px-2 py-1 flex-1 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-blue-400"
                                                autoFocus
                                              />
                                            ) : (
                                              <h5
                                                className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-1 py-0.5 flex-1 break-words"
                                                onClick={() => handleFieldDoubleClick(item.id, 'title')}
                                                title="Click to edit title"
                                              >
                                                {item.title}
                                              </h5>
                                            )}
                                            {/* Details Button - in title row */}
                                            <button
                                              onClick={() => handleViewItemDetails(item.hash, item.id)}
                                              className="ml-2 px-2 py-1 text-xs font-medium rounded flex items-center justify-center bg-obn-lightblue text-obn-blue border border-obn-blue/20 hover:bg-blue-100 hover:border-obn-blue/40 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-400/30 dark:hover:bg-blue-900/60 dark:hover:border-blue-400/50 flex-shrink-0 self-start"
                                              title="View full item details"
                                            >
                                              <ChevronRightIcon className="h-4 w-4" />
                                              <span className="hidden sm:inline ml-1">Details</span>
                                            </button>
                                          </div>

                                          {/* Meta info row - matches ItemDetailPage layout */}
                                          <div className="mb-1 flex items-center flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300 px-1 py-0.5">
                                            {/* Author */}
                                            <div className="flex items-center gap-1" title="Edit authors in item details panel">
                                              <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                              {Array.isArray(item.authors) && item.authors.length > 0 && item.authors[0] ? (
                                                <span>{(() => {
                                                  if (item.authors.length === 1) {
                                                    return item.authors[0];
                                                  } else if (item.authors.length === 2) {
                                                    return `${item.authors[0]} and ${item.authors[1]}`;
                                                  } else {
                                                    // 3 or more authors: use Oxford comma
                                                    const allButLast = item.authors.slice(0, -1);
                                                    const lastAuthor = item.authors[item.authors.length - 1];
                                                    return `${allButLast.join(', ')}, and ${lastAuthor}`;
                                                  }
                                                })()}</span>
                                              ) : (
                                                <span className="text-obn-red font-semibold">NO AUTHOR</span>
                                              )}
                                            </div>
                                            {/* Published date */}
                                            {item.published && (
                                              <div className="flex items-center gap-1" title="Edit in item details panel">
                                                <CalendarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                                <span>{new Date(item.published).toLocaleDateString()}</span>
                                              </div>
                                            )}
                                            {/* Time to read */}
                                            {item.timeToRead != null && (
                                              <div className="flex items-center gap-1">
                                                <ClockIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                                <span>{item.timeToRead} min read</span>
                                              </div>
                                            )}
                                            {/* Quality and Relevance scores with 5-box indicators */}
                                            {(item.qualityScore != null || item.relevanceScore != null) && (
                                              <div className="flex items-center gap-3">
                                                {item.qualityScore != null && (
                                                  <div className="flex items-center gap-1" title={`Quality: ${item.qualityScore.toFixed(1)}/5`}>
                                                    <CheckBadgeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                                    <ScoreDisplay score={item.qualityScore} />
                                                  </div>
                                                )}
                                                {item.relevanceScore != null && (
                                                  <div className="flex items-center gap-1" title={`Relevance: ${item.relevanceScore.toFixed(1)}/5`}>
                                                    <BullseyeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                                    <ScoreDisplay score={item.relevanceScore} />
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>

                                          {/* Editable Instance Blurb */}
                                          <div className="mb-1">
                                            {editingField === `${item.id}-blurb` ? (
                                              <textarea
                                                defaultValue={item.blurb || ''}
                                                onBlur={(e) => handleFieldBlur(item.id, 'blurb', e.target.value, item.blurb || '')}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Escape') {
                                                    setEditingField(null);
                                                  }
                                                }}
                                                className="text-xs text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 dark:placeholder-gray-400 rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-gray-500 dark:focus:ring-blue-400 resize-y min-h-[2.5rem]"
                                                placeholder="Add issue-specific blurb (optional)"
                                                autoFocus
                                              />
                                            ) : (
                                              <div
                                                className="text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2 py-1 min-h-[1.5rem] flex items-center"
                                                onClick={() => handleFieldDoubleClick(item.id, 'blurb')}
                                                title="Click to edit issue blurb"
                                              >
                                                {item.blurb ? (
                                                  <span className="text-gray-700 dark:text-gray-200">{item.blurb}</span>
                                                ) : (
                                                  <span className="text-gray-400 dark:text-gray-500 italic">Add blurb</span>
                                                )}
                                              </div>
                                            )}
                                          </div>

                                          {/* URL (clickable link) */}
                                          <div className="flex items-center min-w-0">
                                            <a
                                              href={item.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-xs text-primary-600 hover:text-primary-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline break-all"
                                              title={item.url}
                                            >
                                              {item.url}
                                            </a>
                                          </div>

                                          {/* Category Section */}
                                          <div className="mt-3 flex items-center space-x-2 flex-wrap">
                                            {/* Quick Category Assignment Buttons (only for items with no category) */}
                                            {(!item.category || item.category === '') && (
                                              <div className="flex items-center flex-wrap gap-1">
                                                {ITEM_CATEGORIES.map((cat) => (
                                                  <button
                                                    key={cat.value}
                                                    onClick={() => handleCategoryChange(item.id, cat.value)}
                                                    disabled={updatingItemId === item.id}
                                                    className={`px-2 py-1 text-xs font-medium border rounded ${cat.color} ${cat.hoverColor} disabled:opacity-50 disabled:cursor-not-allowed`}
                                                  >
                                                    {cat.label}
                                                  </button>
                                                ))}
                                              </div>
                                            )}

                                            {/* Expandable Category Selector (only for items that already have a category) */}
                                            {(item.category && item.category !== '') && (
                                              <div className="category-selector flex items-center gap-1">
                                                {/* Collapsed: Show only selected category + expand arrow */}
                                                {expandedCategorySelector !== item.id && (
                                                  <>
                                                    <button
                                                      disabled={updatingItemId === item.id}
                                                      className={`px-2 py-1 text-xs font-medium border rounded ${getCategoryColor(item.category)} disabled:opacity-50`}
                                                    >
                                                      {getCategoryLabel(item.category)}
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedCategorySelector(item.id);
                                                      }}
                                                      disabled={updatingItemId === item.id}
                                                      className="px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                                                      title="Change category"
                                                    >
                                                      <ChevronRightIcon className="h-3 w-3" />
                                                    </button>
                                                  </>
                                                )}

                                                {/* Expanded: Show all categories with animation */}
                                                {expandedCategorySelector === item.id && (
                                                  <div
                                                    className="flex items-center gap-1"
                                                    style={{ animation: 'slideIn 150ms ease-out' }}
                                                  >
                                                    {/* No Category option */}
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (item.category) {
                                                          handleCategoryChange(item.id, null);
                                                        }
                                                        setExpandedCategorySelector(null);
                                                      }}
                                                      disabled={updatingItemId === item.id}
                                                      className={`px-2 py-1 text-xs font-medium border rounded transition-all bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600
                                                        ${!item.category ? 'ring-2 ring-offset-1 ring-gray-400 dark:ring-offset-gray-800' : ''}
                                                        disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    >
                                                      None
                                                    </button>
                                                    {ITEM_CATEGORIES.map((cat) => (
                                                      <button
                                                        key={cat.value}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (cat.value !== item.category) {
                                                            handleCategoryChange(item.id, cat.value);
                                                          }
                                                          setExpandedCategorySelector(null);
                                                        }}
                                                        disabled={updatingItemId === item.id}
                                                        className={`px-2 py-1 text-xs font-medium border rounded transition-all ${cat.color} ${cat.hoverColor}
                                                          ${cat.value === item.category ? 'ring-2 ring-offset-1 ring-gray-400 dark:ring-offset-gray-800' : ''}
                                                          disabled:opacity-50 disabled:cursor-not-allowed`}
                                                      >
                                                        {cat.label}
                                                      </button>
                                                    ))}
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedCategorySelector(null);
                                                      }}
                                                      className="px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                      title="Collapse"
                                                    >
                                                      <ChevronLeftIcon className="h-3 w-3" />
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                        )}
                                      </SortableItem>
                                      ))
                                      }
                                      </div>
                                    )}
                                  </SortableCategory>
                                )}
                              </div>
                            );
                          });
                          })()}
                        </div>

                        {/* Drag overlay - renders the ghost while dragging */}
                        <DragOverlay dropAnimation={null}>
                          {dndActiveItem ? (
                            <div
                              className="opacity-95 shadow-2xl rounded border-2 border-obn-blue bg-white dark:bg-gray-800 flex pointer-events-none"
                              style={{ width: dndActiveWidth || 'auto' }}
                            >
                              {/* Drag Handle - colored by flag */}
                              <div
                                className={`w-10 flex flex-col items-center rounded-l flex-shrink-0 self-stretch ${
                                  dndActiveItem.flag === 'review' ? 'bg-orange-100 dark:bg-orange-900/40' :
                                  dndActiveItem.flag === 'followup' ? 'bg-purple-100 dark:bg-purple-900/40' :
                                  dndActiveItem.flag === 'new' ? 'bg-green-100 dark:bg-green-900/40' :
                                  dndActiveItem.position ? 'bg-obn-lightblue dark:bg-blue-900/40' : 'bg-red-100 dark:bg-red-900/40'
                                }`}
                              >
                                {/* Flag icon at top */}
                                <div className="pt-3">
                                  {dndActiveItem.flag ? (
                                    <FlagIconSolid className={`h-4 w-4 ${getFlagOption(dndActiveItem.flag)?.color || 'text-gray-400 dark:text-gray-500'}`} />
                                  ) : (
                                    <FlagIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                  )}
                                </div>
                                {/* Drag grip centered in remaining space */}
                                <div className="flex-1 flex items-center justify-center">
                                  <svg className={`w-4 h-4 ${
                                    dndActiveItem.flag === 'review' ? 'text-orange-600' :
                                    dndActiveItem.flag === 'followup' ? 'text-purple-600' :
                                    dndActiveItem.flag === 'new' ? 'text-blue-600' :
                                    dndActiveItem.position ? 'text-obn-blue' : 'text-obn-red'
                                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                  </svg>
                                </div>
                              </div>
                              {/* Content - matches actual card structure */}
                              <div className="flex-1 flex flex-col min-w-0 pl-3 pr-3 py-3">
                                {/* Title row */}
                                <div className="mb-1 flex items-start">
                                  <div className="mr-2 p-1">
                                    {(() => {
                                      const IconComponent = getTypeIcon(dndActiveItem.type);
                                      return <IconComponent className="h-4 w-4 text-gray-500 dark:text-gray-400" />;
                                    })()}
                                  </div>
                                  <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 flex-1 break-words">
                                    {dndActiveItem.title || 'Untitled'}
                                  </h5>
                                </div>
                                {/* Meta info row */}
                                <div className="mb-1 flex items-center flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300 px-1 py-0.5">
                                  <div className="flex items-center gap-1">
                                    <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                    {Array.isArray(dndActiveItem.authors) && dndActiveItem.authors.length > 0 && dndActiveItem.authors[0] ? (
                                      <span>{dndActiveItem.authors.length === 1
                                        ? dndActiveItem.authors[0]
                                        : dndActiveItem.authors.length === 2
                                          ? `${dndActiveItem.authors[0]} and ${dndActiveItem.authors[1]}`
                                          : `${dndActiveItem.authors.slice(0, -1).join(', ')}, and ${dndActiveItem.authors[dndActiveItem.authors.length - 1]}`
                                      }</span>
                                    ) : (
                                      <span className="text-obn-red font-semibold">NO AUTHOR</span>
                                    )}
                                  </div>
                                  {dndActiveItem.published && (
                                    <div className="flex items-center gap-1">
                                      <CalendarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                      <span>{new Date(dndActiveItem.published).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                  {dndActiveItem.timeToRead != null && (
                                    <div className="flex items-center gap-1">
                                      <ClockIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                      <span>{dndActiveItem.timeToRead} min read</span>
                                    </div>
                                  )}
                                  {(dndActiveItem.qualityScore != null || dndActiveItem.relevanceScore != null) && (
                                    <div className="flex items-center gap-3">
                                      {dndActiveItem.qualityScore != null && (
                                        <div className="flex items-center gap-1">
                                          <CheckBadgeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                          <ScoreDisplay score={dndActiveItem.qualityScore} />
                                        </div>
                                      )}
                                      {dndActiveItem.relevanceScore != null && (
                                        <div className="flex items-center gap-1">
                                          <BullseyeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                          <ScoreDisplay score={dndActiveItem.relevanceScore} />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {/* Blurb */}
                                <div className="mb-1">
                                  <div className="text-xs px-2 py-1 min-h-[1.5rem] flex items-center">
                                    {dndActiveItem.blurb ? (
                                      <span className="text-gray-700 dark:text-gray-200">{dndActiveItem.blurb}</span>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 italic">Add blurb</span>
                                    )}
                                  </div>
                                </div>
                                {/* URL */}
                                <div className="flex items-center min-w-0">
                                  <span className="text-xs text-primary-600 dark:text-blue-400 break-all">
                                    {dndActiveItem.url}
                                  </span>
                                </div>
                                {/* Category indicator */}
                                {dndActiveItem.category && (
                                  <div className="mt-3">
                                    <span className={`px-2 py-1 text-xs font-medium border rounded ${getCategoryColor(dndActiveItem.category)}`}>
                                      {getCategoryLabel(dndActiveItem.category)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null}
                        </DragOverlay>
                      </DndContext>
                    ) : (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900">
                        <div className="flex justify-between items-center p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                          <span className="text-base font-semibold text-obn-blue dark:text-white">Markdown Preview</span>
                          <button
                            onClick={handleCopyMarkdown}
                            className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                              copySuccess
                                ? 'bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800/50'
                                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 hover:text-gray-800 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                            }`}
                          >
                            {copySuccess ? (
                              <>
                                <CheckIcon className="h-4 w-4 mr-1" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <ClipboardIcon className="h-4 w-4 mr-1" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                        <div className="p-4">
                          <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">
                            {generateMarkdown(assignedItems.instances)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-3 rounded-md">
                    No items have been assigned to this issue yet.
                  </div>
                )}
              </div>

              {/* Metadata Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <p><span className="font-medium">Created:</span> {new Date(editingIssue.created).toLocaleString()}</p>
                  <p><span className="font-medium">Updated:</span> {new Date(editingIssue.modified).toLocaleString()}</p>
                  <p><span className="font-medium">Author:</span> {editingIssue.authorName}</p>
                  <p><span className="font-medium">ID:</span> {editingIssue.id}</p>
                  <p>
                    <span className="font-medium">Single Send:</span>{' '}
                    {editingIssue.singleSendId || <span className="italic">no single send</span>}
                    {editingIssue.singleSendId && editingIssue.status === 'unpublished' && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => {
                            updateMutation.mutate({
                              id: editingIssue.issueNumber,
                              singleSendId: '',
                            });
                          }}
                          disabled={updateMutation.isPending}
                          className="ml-1 text-obn-red dark:text-red-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Clear the SendGrid Single Send ID from this issue (the Single Send in SendGrid is not deleted)"
                        >
                          Reset
                        </button>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
        </div>
      </div>

      {/* Item Details Slide-over Panel (in editingIssue view) */}
      {slideOverItemHash && (
        <SlideOverPanel
          isOpen={isSlideOverOpen}
          onClose={closeSlideOver}
          title="Item Details"
          onNavigatePrevious={navigatePrevious}
          onNavigateNext={navigateNext}
          position={getPosition()}
        >
          <ItemDetailPage />
        </SlideOverPanel>
      )}
      </>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-obn-blue dark:text-white">Newsletter Issues</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Manage your newsletter issues and track their performance</p>
        </div>
        {!isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
          >
            📰 Create Issue
          </button>
        )}
      </div>
      
      {isCreating && (
        <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-obn-blue dark:text-white mb-4">
            Create New Newsletter Issue
          </h2>
          <form onSubmit={handleCreateIssue} className="space-y-4">
            <div>
              <label htmlFor="issueNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Issue Number (optional)
              </label>
              <input
                type="number"
                name="issueNumber"
                id="issueNumber"
                value={newIssueNumber}
                onChange={(e) => setNewIssueNumber(e.target.value)}
                placeholder="Auto-generated if left blank"
                className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                min="1"
                step="1"
                autoFocus
              />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                If not specified, the next available number will be used. The issue will be created with a default title "Issue #[number]" that you can edit after creation.
              </p>
            </div>

            <div>
              <label htmlFor="issueDate" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Issue Date (optional)
              </label>
              <input
                type="date"
                name="issueDate"
                id="issueDate"
                value={newIssueDate}
                onChange={(e) => setNewIssueDate(e.target.value)}
                className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsCreating(false);
                  setNewIssueNumber('');
                  setNewIssueDate('');
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : 'Create Issue'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {!isCreating && (
        <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {issuesData?.issues?.map((issue) => (
              <li key={issue.id}>
                <div
                  className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200"
                  onClick={() => navigate(`/issues/${issue.issueNumber}`)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-8">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">#{issue.issueNumber}</span>
                        <p className="text-sm font-medium text-obn-blue dark:text-white truncate">{issue.title}</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          issue.status === 'published' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
                        }`}>
                          {issue.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </div>

                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{issue.description}</p>

                      {issue.subject && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span className="font-medium">Subject:</span> {issue.subject}
                        </p>
                      )}

                      <div className="mt-2 flex items-center flex-wrap text-xs text-gray-500 dark:text-gray-400 space-x-4">
                        {issue.issueDate ? (
                          <span>{new Date(issue.issueDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                        ) : (
                          <span>{new Date(issue.created).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                        )}

                        {issue.tags && issue.tags.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <span>Tags:</span>
                            {issue.tags.map((tag, index) => (
                              <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {issue.status === 'published' && issue.deliveredCount > 0 && (
                      <div className="flex items-center gap-5 shrink-0 text-xs">
                        <div className="text-center">
                          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">{(issue.deliveredCount || 0).toLocaleString()}</div>
                          <div className="text-gray-400 dark:text-gray-500">delivered</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-semibold text-green-600 dark:text-green-400">{((issue.uniqueOpenCount / issue.deliveredCount) * 100).toFixed(1)}%</div>
                          <div className="text-gray-400 dark:text-gray-500">opens</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-semibold text-obn-blue dark:text-blue-400">{((issue.uniqueClickCount / issue.deliveredCount) * 100).toFixed(1)}%</div>
                          <div className="text-gray-400 dark:text-gray-500">clicks</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">{issue.unsubscribeCount || 0}</div>
                          <div className="text-gray-400 dark:text-gray-500">unsubs</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {(!issuesData?.issues || issuesData.issues.length === 0) && (
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <NewspaperIcon className="h-16 w-16 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-gray-500 dark:text-gray-400">No newsletter issues yet. Create your first issue!</p>
            </div>
          )}
        </div>
      )}

      {/* Item Details Slide-over Panel */}
      {slideOverItemHash && (
        <SlideOverPanel
          isOpen={isSlideOverOpen}
          onClose={closeSlideOver}
          title="Item Details"
          onNavigatePrevious={navigatePrevious}
          onNavigateNext={navigateNext}
          position={getPosition()}
        >
          <ItemDetailPage />
        </SlideOverPanel>
      )}

      {/* News Section Generator Modal */}
      <NewsSectionGeneratorModal
        isOpen={showNewsSectionGenerator}
        onClose={() => {
          setShowNewsSectionGenerator(false);
          setGeneratedNewsBlurbs([]);
          setNewsSectionError(null);
        }}
        items={(assignedItems?.instances || [])
          .filter((inst) => inst.category === 'news')
          .map((inst) => ({
            hash: extractItemHash(inst.id),
            title: inst.title,
            summary: inst.summary,
          }))}
        onGenerate={handleGenerateNewsSection}
        onApplyAll={handleApplyAllNewsBlurbs}
        onApplySingle={handleApplySingleNewsBlurb}
        isGenerating={isGeneratingNewsSection}
        generatedBlurbs={generatedNewsBlurbs}
        error={newsSectionError}
      />

    </div>
  );
}

export default IssuesPage;